import React, { useState, useEffect } from 'react';
import {
  Search, Plus, Download,
  Package, List, LayoutGrid,
  ArrowUpAZ, ArrowDownZA,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from 'react-router-dom';
import { getLoadList } from '../../redux/Load/actions';
import FileUpload from '../FileUpload/FileUpload';
import { DataTable } from '../ui/table';
import { getLoadPlanColumns } from './columns';
import { useDebounced } from '@/hooks/useDebounced';
import { callLoader } from '@/redux/Loader/actions';
import OrderCard, { LOAD_PLAN_MAP } from '../orderCard/OrderCard';

const TITLE = "Load Plan Management";
function AnimatedTitle() {
  return (
    <motion.h1
      initial={{ opacity: 0, y: 8, filter: "blur(6px)" }}
      animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      className="text-[18px] sm:text-[20px] font-bold tracking-tight leading-tight text-gray-900 dark:text-white"
    >
      <motion.span
        className="inline-block overflow-hidden whitespace-nowrap align-bottom"
        initial={{ width: "0ch" }}
        animate={{ width: ["0ch", `${TITLE.length}ch`, `${TITLE.length}ch`, "0ch"] }}
        transition={{ duration: 4.8, times: [0, 0.22, 0.9, 1], ease: ["linear", "linear", "easeInOut"], repeat: Infinity }}
      >
        {TITLE}
      </motion.span>
    </motion.h1>
  );
}

function ViewToggle({ view, setView }) {
  const base = "w-9 h-9 flex items-center justify-center rounded-md transition-colors";
  const active = "bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400";
  const inactive = "text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100";
  return (
    <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-0.5">
      <button onClick={() => setView("table")} title="Table view" className={`${base} ${view === "table" ? active : inactive}`}>
        <List className="w-4 h-4" />
      </button>
      <button onClick={() => setView("card")} title="Card view" className={`${base} ${view === "card" ? active : inactive}`}>
        <LayoutGrid className="w-4 h-4" />
      </button>
    </div>
  );
}

const LoadPlanList = ({ onCreateNew, onEditPlan }) => {
  const navigate = useNavigate();

  // ── All original state (unchanged) ────────────────────────────────────────
  const [searchText, setSearchText]               = useState(null);
  const [searchField, setSearchField]             = useState('all');
  const [selectedPlans, setSelectedPlans]         = useState([]);
  const [currentPage, setCurrentPage]             = useState(1);
  const [pageSize, setPageSize]                   = useState(10);
  const [fileUploadVisible, setFileUploadVisible] = useState(false);
  const [selectedOrderId, setSelectedOrderId]     = useState(null);

  // ── UI-only state ──────────────────────────────────────────────────────────
  const [view, setView]                   = useState("table");
  const [sortBy, setSortBy]               = useState("recent");
  const [sortDirection, setSortDirection] = useState("desc");

  const { loadList, pagination } = useSelector((state) => state.load);
  const dispatch = useDispatch();
  const [authUser] = JSON.parse(localStorage.getItem('authUser') ?? "[{}]");
  const companyId  = authUser?.companyId;

  // ── Original debounced search (unchanged) ──────────────────────────────────
  const debouncedSearch = useDebounced(() => { _getLoadList(1); }, 300);
  useEffect(() => { if (searchText === null) return; debouncedSearch(searchText); }, [searchText]);
  useEffect(() => { _getLoadList(1); }, [pageSize]);
  useEffect(() => {
    if (selectedOrderId != null) setFileUploadVisible(true);
    else setFileUploadVisible(false);
  }, [selectedOrderId]);

  // ── Original fetch (unchanged) ─────────────────────────────────────────────
  const _getLoadList = async (page = 1) => {
    const payload = { IPageNo: page, IPageRecords: pageSize, FilterType: searchField, SearchKeyword: searchText || "", compnayId: companyId };
    try {
      dispatch(callLoader(1));
      await dispatch(getLoadList(payload));
      setCurrentPage(page);
      dispatch(callLoader(0));
    } catch (error) { dispatch(callLoader(0)); }
  };

  // ── Original handlers (unchanged) ──────────────────────────────────────────
  const handlePageSize = (size) => setPageSize(size);
  const changePage     = (page) => _getLoadList(page);
  const handleSelectAll  = (e) => { if (e.target.checked) setSelectedPlans(loadList?.map(p => p.loId)); else setSelectedPlans([]); };
  const handleSelectPlan = (planId) => {
    if (selectedPlans.includes(planId)) setSelectedPlans(selectedPlans.filter(id => id !== planId));
    else setSelectedPlans([...selectedPlans, planId]);
  };

  const columns = getLoadPlanColumns({ selectedPlans, loadList, handleSelectAll, handleSelectPlan, onEditPlan, setSelectedOrderId });

  const statusOptions = [
    { value: "all", label: "All" }, { value: "active", label: "Active" }, { value: "completed", label: "Completed" },
  ];

  return (
    <div className="min-h-full space-y-3 p-2 sm:p-3 lg:p-4">

      {/* HEADER CARD */}
      <div className="rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
        <div className="px-4 sm:px-5 py-4 flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-3 min-w-0">
            <div className="shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-blue-50 dark:bg-blue-900/40 ring-1 ring-blue-100 dark:ring-blue-800 flex items-center justify-center">
              <Package className="w-5 h-5 text-blue-600 dark:text-blue-400" strokeWidth={2.2} />
            </div>
            <div className="min-w-0">
              <AnimatedTitle />
              <p className="mt-0.5 text-[12px] text-gray-500 dark:text-gray-400 hidden sm:block">
                {pagination?.totalRecord ?? 0} total load plans
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <button className="inline-flex items-center gap-1.5 h-9 px-3 sm:px-3.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 text-[12.5px] font-semibold transition-colors">
              <Download className="w-3.5 h-3.5" />
              <span className="hidden xs:inline">Export</span>
            </button>
            <button onClick={onCreateNew} className="inline-flex items-center gap-1.5 h-9 px-3 sm:px-3.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[12.5px] font-semibold shadow-[0_4px_14px_-4px_rgba(37,99,235,0.45)] transition-colors">
              <Plus className="w-3.5 h-3.5" />
              <span className="hidden xs:inline">New Load Plan</span>
              <span className="xs:hidden">New</span>
            </button>
            <ViewToggle view={view} setView={setView} />
          </div>
        </div>
      </div>

      {/* FILTER BAR */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm px-4 sm:px-5 py-4 space-y-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 flex items-center gap-2">
            <select value={searchField} onChange={(e) => setSearchField(e.target.value)}
              className="px-3 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-[12.5px] text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 shrink-0">
              <option value="all">All Fields</option>
              <option value="loadplan">Load Plan #</option>
              <option value="shipment">Shipment #</option>
              <option value="status">Status</option>
            </select>
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by load plan number, shipment..."
                value={searchText ?? ""}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-xl p-1">
              {statusOptions.map(({ value, label }) => (
                <button key={value} onClick={() => setSearchField(value)}
                  className={`px-3 py-1.5 rounded-lg text-[12.5px] font-medium transition-all ${searchField === value ? "bg-blue-600 text-white shadow-sm" : "text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700"}`}>
                  {label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1.5">
              <button onClick={() => setSortDirection((d) => (d === "asc" ? "desc" : "asc"))}
                className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
                {sortDirection === "asc" ? <ArrowUpAZ className="w-4 h-4 text-gray-500" /> : <ArrowDownZA className="w-4 h-4 text-gray-500" />}
              </button>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-[12.5px] text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="recent">Most Recent</option>
                <option value="planno">Plan Number</option>
                <option value="status">Status</option>
              </select>
            </div>
          </div>
        </div>
        <div className="pt-3 border-t border-gray-100 dark:border-gray-800">
          <p className="text-[12.5px] text-gray-500 dark:text-gray-400">
            Showing <span className="font-medium text-gray-700 dark:text-gray-300">{pagination?.currentRecord || 0}</span>{" "}
            of <span className="font-medium text-gray-700 dark:text-gray-300">{pagination?.totalRecord || 0}</span> load plans
          </p>
        </div>
      </div>

      {/* CONTENT */}
      <div className="p-1">
        {(loadList || []).length === 0 ? (
          <div className="text-center py-20 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800">
            <Package className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-300 font-semibold text-lg">No load plans found</p>
            <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">Try adjusting your search or filter criteria</p>
            <button onClick={onCreateNew} className="mt-4 px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors">
              + New Load Plan
            </button>
          </div>

        ) : view === "card" ? (
          /* ── Card grid — shared OrderCard with LOAD_PLAN_MAP ── */
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(loadList || []).map((plan) => (
              <OrderCard
                key={plan.loId}
                record={plan}
                fieldMap={LOAD_PLAN_MAP}
            
                navigate={navigate}
                t={(k) => k}
                onEdit={onEditPlan}
                onOpenFileUpload={() => setSelectedOrderId(plan)}
                onOpenNotes={() => {}}
                onDelete={() => {}}
                onRestore={() => {}}
              />
            ))}
          </div>

        ) : (
          /* ── Table view — original DataTable (unchanged) ── */
           <div className="space-y-4">
            <AnimatePresence>
              {(loadList || []).map((plan) => (
                <OrderCard
                 key={plan.loId}
                record={plan}
                fieldMap={LOAD_PLAN_MAP}
            
                navigate={navigate}
                t={(k) => k}
                onEdit={onEditPlan}
                onOpenFileUpload={() => setSelectedOrderId(plan)}
                onOpenNotes={() => {}}
                onDelete={() => {}}
                onRestore={() => {}}
                />
              ))}
            </AnimatePresence>
          </div>
        )}

        {/* PAGINATION */}
        {(loadList || []).length > 0 && (
          <div className="border-t border-gray-200 dark:border-gray-800 pl-2 pr-16 pt-4 pb-8 mb-4 mt-8 bg-gray-50 dark:bg-gray-800/50 flex items-center justify-between rounded-b-xl">
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <span>Show</span>
              <select value={pageSize} onChange={(e) => handlePageSize(Number(e.target.value))}
                className="px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500">
                {[5, 10, 25, 50].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <span>entries</span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => currentPage > 1 && changePage(currentPage - 1)} disabled={currentPage === 1}
                className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40 transition-colors">
                Previous
              </button>
              <button className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg cursor-default">{currentPage}</button>
              <button onClick={() => pagination?.nextPage > 0 && changePage(pagination.nextPage)} disabled={!pagination?.nextPage || pagination.nextPage === 0}
                className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40 transition-colors">
                Next
              </button>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Showing <span className="font-medium">{pagination?.currentRecord || 0}</span>{" "}
              of <span className="font-medium">{pagination?.totalRecord || 0}</span> load plans
            </p>
          </div>
        )}
      </div>

      {/* FILE UPLOAD MODAL (original logic unchanged) */}
      {fileUploadVisible && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 w-[70%] p-6 rounded-xl shadow-xl animate-fadeIn scale-100">
            <FileUpload orderId={selectedOrderId?.loId} orderNum={selectedOrderId?.loNumber} type="loadplan" />
            <div className="flex justify-end mt-6">
              <button onClick={() => { setFileUploadVisible(false); setSelectedOrderId(null); }}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-lg text-sm font-medium transition-colors">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoadPlanList;